import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { usePubNub } from "pubnub-react";
import {
  setActiveConversation,
  setConversationMessages,
  addMessageToConversation,
  setLoadingConversationMessages,
  setSendingConversationMessage,
  setRealTimeEnabled,
} from "../redux/chatSlice";
import { getConversationMessages, saveConversationMessage } from "../services/community";

export const useConversationChat = (mentorId, menteeId, currentUserId) => {
  const dispatch = useDispatch();
  const pubnub = usePubNub();
  const [isConnected, setIsConnected] = useState(false);
  const { conversations, activeConversation, isLoadingConversationMessages, isSendingConversationMessage } =
    useSelector((s) => s.chat);

  const conversationId = mentorId && menteeId ? [mentorId, menteeId].sort().join("_") : null;
  const currentMessages = conversationId ? conversations[conversationId] || [] : [];
  const currentUserType = currentUserId === mentorId ? "mentor" : "mentee";

  const initializeConversation = useCallback(async () => {
    if (!conversationId) return;
    dispatch(setActiveConversation({ mentorId, menteeId, conversationId }));
    dispatch(setLoadingConversationMessages(true));
    try {
      const { data } = await getConversationMessages(mentorId, menteeId);
      const transformed = data?.map((msg, i) => ({
        id: msg._id || i,
        text: msg.message,
        type: msg.senderType === currentUserType ? "sent" : "received",
        senderName: msg.senderType === "mentee" ? "Mentee" : "Mentor",
        senderProfile: msg.senderId?.profilePicture || null,
        timestamp: msg.createdAt,
        senderId: msg.senderId?._id || msg.senderId,
        senderType: msg.senderType,
      }));
      if (transformed) dispatch(setConversationMessages({ conversationId, messages: transformed }));
    } catch (err) {
      console.error("Error fetching messages:", err);
    } finally {
      dispatch(setLoadingConversationMessages(false));
    }
  }, [conversationId, currentUserType, mentorId, menteeId, dispatch]);

  /** Setup Real-time Messaging **/
  const setupRealTimeMessaging = useCallback(() => {
    if (!pubnub || !conversationId) return;
    const channel = `conversation_${conversationId}`;
    pubnub.subscribe({ channels: [channel] });
    setIsConnected(true);
    dispatch(setRealTimeEnabled(true));

    const handleMessage = ({ channel: ch, message, timetoken }) => {
      if (ch !== channel || message.senderType === currentUserType) return;
      dispatch(
        addMessageToConversation({
          conversationId,
          message: {
            id: timetoken,
            text: message.text,
            type: "received",
            senderName: message.senderType === "mentee" ? "Mentee" : "Mentor",
            senderProfile: message.senderProfile || null,
            timestamp: new Date(timetoken / 10000),
            senderId: message.senderId,
            senderType: message.senderType,
          },
        })
      );
    };

    pubnub.addListener({ message: handleMessage });
    return () => {
      pubnub.removeListener({ message: handleMessage });
      pubnub.unsubscribe({ channels: [channel] });
      setIsConnected(false);
      dispatch(setRealTimeEnabled(false));
    };
  }, [pubnub, conversationId, currentUserType, dispatch]);

  /** Send Message **/
  const sendMessage = useCallback(
    async (text) => {
      if (!text.trim() || !conversationId || isSendingConversationMessage) return;
      dispatch(setSendingConversationMessage(true));

      const msgType = currentUserId === mentorId ? "mentor" : "mentee";
      const receiverId = msgType === "mentor" ? menteeId : mentorId;
      const receiverType = msgType === "mentor" ? "mentee" : "mentor";
      const message = {
        id: Date.now(),
        text,
        type: "sent",
        senderName: "You",
        timestamp: new Date(),
        senderId: currentUserId,
        senderType: msgType,
      };
      const channel = `conversation_${conversationId}`;
      dispatch(addMessageToConversation({ conversationId, message }));

      try {
        await pubnub.publish({ channel, message: { ...message, receiverId, receiverType } });
        await saveConversationMessage(mentorId, menteeId, msgType, receiverType, text);
      } catch (err) {
        console.error("Error sending message:", err);
      } finally {
        dispatch(setSendingConversationMessage(false));
      }
    },
    [mentorId, menteeId, conversationId, isSendingConversationMessage, pubnub, dispatch]
  );

  /** Lifecycle **/
  useEffect(() => {
    if (mentorId && menteeId) initializeConversation();
  }, [mentorId, menteeId, initializeConversation]);

  useEffect(() => {
    if (conversationId) return setupRealTimeMessaging();
  }, [conversationId, setupRealTimeMessaging]);

  return {
    messages: currentMessages,
    isLoadingConversationMessages,
    isSendingConversationMessage,
    isConnected,
    sendMessage,
    conversationId,
    activeConversation,
  };
};
